import { faFilePdf, faGraduationCap, faStamp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Pagination, Paper, Select, Skeleton, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useLang } from '../../../../language';
import { InputText } from '../../../../components/Inputs/InputText';
import { FacturasServices } from '../../controllers/services/facturas.services';

/**
 * Datos de muestra para el futuro sistema de facturas de visado (tecnovisa).
 * Preparado para sustituir por la llamada al servicio cuando esté disponible.
 */
const FacturasVisadoDummy = [
    {
        numFactura: 'VIS-2026-0001',
        concepto: 'Certificado de visado 2026',
        fecha: '15/06/2026',
        importe: '120,00 €',
        estado: 'pagada',
    },
    {
        numFactura: 'VIS-2026-0002',
        concepto: 'Renovación de visado',
        fecha: '02/07/2026',
        importe: '95,00 €',
        estado: 'pendiente',
    },
]

/**
 * Facturas de la empresa por sistema: Actividades (integra-web, accion
 * `obtener_facturas`) y Visado (tecnovisa, dummy de momento).
 */
export const Facturas = ({ setBreadcrumbLineItems, route, empresa }: any) => {

    const Lang = useLang()

    const { empresaId } = useParams()

    // CIF/NIF de la empresa (lo carga EmpresaDetalle) para localizar el cliente
    // en el WS d'Activitats (igual que en listar_inscripciones).
    const cif = empresa?.cif || ''

    const [tabActivo, setTabActivo] = useState(0)

    const [paginacion, setPaginacion] = useState({ page: 1, last_page: 1 })

    // Filtros del listado de facturas de actividades (con debounce)
    const [buscar, setBuscar] = useState('')
    const [numeroFactura, setNumeroFactura] = useState('')
    const [tipoFactura, setTipoFactura] = useState('')
    const [filtrosAplicados, setFiltrosAplicados] = useState<any>({ buscar: '', numero_factura: '', tipo_factura: '' })

    // Facturas del sistema d'Activitats: accion=obtener_facturas
    const [fetchFacturasGET, fetchFacturasGETData] = FacturasServices.OBTENER_FACTURAS()

    // Tipos de factura para el filtro: accion=obtener_tiposfacturas
    const [fetchTiposGET, fetchTiposGETData] = FacturasServices.GET_TIPOS_FACTURA()

    const facturas = fetchFacturasGETData.data?.items || []

    const tipos = fetchTiposGETData.data?.tipos || []

    // Loading inmediato: `loading` empieza en `null` (la primera petición espera
    // al CIF de la empresa). Tratamos null como "cargando" para que el skeleton
    // aparezca al instante y no haya un hueco en blanco.
    const cargando = fetchFacturasGETData.loading !== false

    const error = fetchFacturasGETData.error

    useEffect(() => {

        setBreadcrumbLineItems((item: any) => [...item.slice(0, 2), route])

    }, [])

    // Catálogo de tipos de factura (se carga una vez al montar)
    useEffect(() => {

        fetchTiposGET({})

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Filtros con debounce (solo afecta al listado de actividades)
    //
    // `filtrosAplicados` solo se sustituye si algún valor ha cambiado de verdad. Es
    // un objeto, y el efecto del listado lo tiene entre sus dependencias: al
    // montar, este debounce devuelve los mismos valores que ya tiene (vacíos) pero
    // en un objeto nuevo, lo que disparaba una segunda petición idéntica a la
    // primera. Con la comprobación, al montar no se vuelve a pedir nada.
    useEffect(() => {

        const handler = setTimeout(() => {

            const nuevos = {
                buscar: buscar.trim(),
                numero_factura: numeroFactura.trim(),
                tipo_factura: tipoFactura,
            }

            setFiltrosAplicados((prev: any) => {

                const sinCambios
                    = prev.buscar === nuevos.buscar
                    && prev.numero_factura === nuevos.numero_factura
                    && prev.tipo_factura === nuevos.tipo_factura

                return sinCambios ? prev : nuevos

            })

            setPaginacion((prev: any) => (prev.page === 1 ? prev : { ...prev, page: 1 }))

        }, 400)

        return () => clearTimeout(handler)

    }, [buscar, numeroFactura, tipoFactura])

    // Listado de facturas de actividades (espera al CIF de la empresa)
    useEffect(() => {

        if (!empresaId || !cif) return

        fetchFacturasGET({
            empresa_id: Number(empresaId),
            cif,
            buscar: filtrosAplicados.buscar,
            numero_factura: filtrosAplicados.numero_factura,
            tipo_factura: filtrosAplicados.tipo_factura,
            page: paginacion.page,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresaId, cif, filtrosAplicados, paginacion.page])

    // Sincroniza la página seleccionada con la respuesta del WS. Como `paginacion`
    // está en las dependencias del listado, aquí solo se fija un objeto nuevo cuando
    // los valores cambian de verdad; si no, la respuesta de cada página provocaría
    // un render extra y, en el peor caso, otra petición.
    useEffect(() => {

        const pag = fetchFacturasGETData.data?.paginacion

        if (pag?.page == null) return

        const page = Number(pag.page)
        const last_page = Number(pag.last_page || pag.page)

        setPaginacion((prev: any) => (
            prev.page === page && prev.last_page === last_page ? prev : { page, last_page }
        ))

    }, [fetchFacturasGETData.data])

    const handleCambioTab = (event: any, nuevo: number) => setTabActivo(nuevo)

    const handleCambioTipo = (event: any) => setTipoFactura(event.target.value)

    const handleCambioPagina = (event: any, nuevaPagina: number) => {

        setPaginacion((prev: any) => ({ ...prev, page: nuevaPagina }))
    }

    // El PDF: si la factura trae `fa_ref_pdf` se abre esa URL; si no, el enlace de
    // generación en el WS (plantillas/generar_factura.php).
    const enlacePdf = (item: any) => item?.refPdf || item?.enlaceGeneracion

    const chipEstadoVisado = (estado: string) => {

        const pagada = estado === 'pagada'

        return (
            <Chip
                label={pagada ? Lang('VISADO_PAGADA') : Lang('VISADO_PENDIENTE')}
                size="small"
                color={pagada ? 'success' : 'warning'}
                variant="outlined"
            />
        )
    }

    return (
        <Paper className='p-3 p-md-4' sx={{ borderRadius: 2.5 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faFilePdf} className='color-primary' />
                <Typography variant='h6' className="my-0 fw-500 color-primary">
                    {Lang('FACTURAS')}
                </Typography>
            </Box>

            <Tabs
                value={tabActivo}
                onChange={handleCambioTab}
                className='mt-2'
                aria-label="facturas por sistema"
            >
                <Tab
                    icon={<FontAwesomeIcon icon={faGraduationCap} />}
                    iconPosition="start"
                    label={Lang('FACTURAS_ACTIVIDADES')}
                />
                <Tab
                    icon={<FontAwesomeIcon icon={faStamp} />}
                    iconPosition="start"
                    label={Lang('FACTURAS_VISADO')}
                />
            </Tabs>

            {
                tabActivo === 0 &&

                <Box className='mt-3'>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            alignItems: 'end',
                        }}
                        className='mb-2'
                    >
                        <InputText
                            label={Lang('BUSCAR')}
                            name='buscar'
                            value={buscar}
                            onChange={(e: any) => setBuscar(e.target.value)}
                        />

                        <InputText
                            label={Lang('NUM_FACTURA')}
                            name='numeroFactura'
                            value={numeroFactura}
                            onChange={(e: any) => setNumeroFactura(e.target.value)}
                        />

                        <FormControl fullWidth variant="standard" size="small">
                            <InputLabel id="select-tipo-factura">{Lang('TIPO_FACTURA')}</InputLabel>
                            <Select
                                labelId="select-tipo-factura"
                                label={Lang('TIPO_FACTURA')}
                                value={tipoFactura}
                                onChange={handleCambioTipo}
                            >
                                <MenuItem value="">{Lang('TODOS_TIPOS')}</MenuItem>
                                {
                                    tipos.map((t: any) => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Box>

                    {
                        !cargando && error &&
                        <Typography variant='caption' className='my-0 mb-2' sx={{ color: 'error.main', display: 'block', textAlign: 'center' }}>
                            {String(error?.message || error)}
                        </Typography>
                    }

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{Lang('ID')}</TableCell>
                                    <TableCell>{Lang('NUM_FACTURA')}</TableCell>
                                    <TableCell>{Lang('ACTIVIDAD')}</TableCell>
                                    <TableCell>{Lang('FECHA_FACTURA')}</TableCell>
                                    <TableCell>{Lang('TIPO_FACTURA')}</TableCell>
                                    <TableCell align="right">{Lang('ACCIONES')}</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>

                                {
                                    cargando &&

                                    Array.from({ length: 5 }).map((_, index) => (
                                        <TableRow key={`skeleton-${index}`}>
                                            <TableCell><Skeleton variant="text" width={70} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={110} /></TableCell>
                                            <TableCell><Skeleton variant="text" /></TableCell>
                                            <TableCell><Skeleton variant="text" width={90} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={80} /></TableCell>
                                            <TableCell align="right" width={110}><Skeleton variant="rounded" height={32} /></TableCell>
                                        </TableRow>
                                    ))
                                }

                                {
                                    !cargando && facturas.map((item: any) => (
                                        <TableRow key={item.id || item.numFactura}>
                                            <TableCell>{item.id}</TableCell>
                                            <TableCell>{item.numFactura}</TableCell>
                                            <TableCell>{item.actividad}</TableCell>
                                            <TableCell>{item.fecha}</TableCell>
                                            <TableCell>{item.tipo}</TableCell>
                                            <TableCell align="right">
                                                {
                                                    enlacePdf(item) &&
                                                    <Button
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        startIcon={<FontAwesomeIcon icon={faFilePdf} style={{ fontSize: 11 }} />}
                                                        href={enlacePdf(item)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        PDF
                                                    </Button>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }

                                {
                                    !cargando && !error && facturas.length === 0 &&

                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Box sx={{ textAlign: 'center', py: 5 }}>
                                                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                                    {Lang('SENSE_FACTURES')}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                }

                            </TableBody>
                        </Table>
                    </TableContainer>

                    {
                        fetchFacturasGETData.loading !== null && (facturas.length > 0 || fetchFacturasGETData.loading === true) &&
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                {paginacion.page} de {paginacion.last_page}
                            </Typography>
                            {
                                paginacion.last_page > 1 &&
                                <Pagination
                                    page={paginacion.page}
                                    count={paginacion.last_page}
                                    onChange={handleCambioPagina}
                                    color="primary"
                                    size="small"
                                />
                            }
                        </Box>
                    }

                </Box>
            }

            {
                tabActivo === 1 &&

                <Box className='mt-3'>

                    <Alert severity="info" icon={<FontAwesomeIcon icon={faStamp} style={{ fontSize: 13 }} />}>
                        {Lang('VISADO_DUMMY_INFO')}
                    </Alert>

                    <TableContainer className='mt-2'>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{Lang('NUM_FACTURA')}</TableCell>
                                    <TableCell>{Lang('CONCEPTO')}</TableCell>
                                    <TableCell>{Lang('FECHA_FACTURA')}</TableCell>
                                    <TableCell>{Lang('IMPORTE')}</TableCell>
                                    <TableCell>{Lang('ESTADO')}</TableCell>
                                    <TableCell align="right">{Lang('ACCIONES')}</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {
                                    FacturasVisadoDummy.map((item: any) => (
                                        <TableRow key={item.numFactura}>
                                            <TableCell>{item.numFactura}</TableCell>
                                            <TableCell>{item.concepto}</TableCell>
                                            <TableCell>{item.fecha}</TableCell>
                                            <TableCell>{item.importe}</TableCell>
                                            <TableCell>{chipEstadoVisado(item.estado)}</TableCell>
                                            <TableCell align="right">
                                                <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                                                    —
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>

                </Box>
            }

        </Paper>
    )
}